/**
 * AdminAnalytics — Phase A-5
 * KPI temps réel (refresh 60s) + 6 graphiques Recharts + journal d'erreurs.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, Eye, Clock, LogIn, BarChart3, AlertTriangle, RefreshCw,
  CheckCircle2, Download as DownloadIcon,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  fetchAnalyticsKpis, fetchTraffic30d, fetchTopPages,
  fetchFeatureUsage, fetchAnalyticsCountries, fetchDevices, fetchFunnel,
  fetchErrors, resolveErrorLog,
} from "@/lib/admin-api";

const COLORS = ["#7f1d1d", "#d97706", "#1d4ed8", "#059669", "#7c3aed", "#ea580c"];
const fmtNum = (n) => (n ?? 0).toLocaleString("fr-FR");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");

const COUNTRY_FLAGS = {
  FR: "🇫🇷", TN: "🇹🇳", SN: "🇸🇳", MR: "🇲🇷", MA: "🇲🇦", DZ: "🇩🇿",
  BE: "🇧🇪", CH: "🇨🇭", CA: "🇨🇦", US: "🇺🇸", GB: "🇬🇧", DE: "🇩🇪",
  CI: "🇨🇮", ML: "🇲🇱", BF: "🇧🇫", CM: "🇨🇲",
};
const flag = (cc) => COUNTRY_FLAGS[cc?.toUpperCase()] || "🌍";

function Kpi({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const tones = {
    default: "bg-muted/30",
    warn: "bg-amber-50 dark:bg-amber-950/20 border-amber-300/60",
    bad: "bg-red-50 dark:bg-red-950/20 border-red-300/60",
    good: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300/60",
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="mt-1 font-mono text-2xl font-bold">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function AdminAnalytics() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try { setKpis(await fetchAnalyticsKpis()); }
    catch { toast.error("Erreur de chargement KPI."); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Analytics visiteurs</h1>
        <span className="ml-auto text-xs text-muted-foreground">
          Rafraîchissement auto · 60 s
        </span>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="analytics-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={Users} label="Visiteurs actifs" value={fmtNum(kpis?.active_now)}
          sub="Sessions < 5 min" tone="good" testId="kpi-active" />
        <Kpi icon={Eye} label="Pages vues aujourd'hui" value={fmtNum(kpis?.views_today)}
          sub={`${fmtNum(kpis?.views_week)} sur 7 j · ${fmtNum(kpis?.views_month)} ce mois`} testId="kpi-views" />
        <Kpi icon={Clock} label="Durée moy. session" value={`${kpis?.avg_session_minutes ?? 0} min`}
          sub={`Rebond : ${kpis?.bounce_rate_pct ?? 0} %`} testId="kpi-duration" />
        <Kpi icon={LogIn} label="Inscriptions (mois)" value={fmtNum(kpis?.signups_month)}
          sub={`${fmtNum(kpis?.analyses_month)} analyses complètes`} testId="kpi-signups" />
      </div>

      <TrafficChart />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TopPagesChart />
        <FeatureUsageChart />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CountriesChart />
        <DevicesChart />
      </div>
      <FunnelChart />
      <ErrorsTable />
    </div>
  );
}

/* --- Trafic 30j --- */
function TrafficChart() {
  const [data, setData] = useState([]);
  useEffect(() => { fetchTraffic30d().then(setData).catch(() => {}); }, []);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Trafic — 30 derniers jours</CardTitle></CardHeader>
      <CardContent className="h-64" data-testid="chart-traffic">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="date" fontSize={10} tickFormatter={(d) => d.slice(5)} />
            <YAxis fontSize={10} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="visits" stroke="#1d4ed8" strokeWidth={2} name="Visites" dot={false} />
            <Line type="monotone" dataKey="signups" stroke="#d97706" strokeWidth={2} name="Inscriptions" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

/* --- Top pages --- */
function TopPagesChart() {
  const [data, setData] = useState([]);
  useEffect(() => { fetchTopPages().then(setData).catch(() => {}); }, []);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Pages les plus visitées (30 j)</CardTitle></CardHeader>
      <CardContent className="h-64" data-testid="chart-pages">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            Aucune donnée. Instrumentez les pages via <code>pingPageView</code>.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 100, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis type="number" fontSize={10} />
              <YAxis type="category" dataKey="path" fontSize={10} width={150} />
              <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, n) => n === "views" ? [`${v} vues`, "Vues"] : [`${v}s`, "Durée moy"]} />
              <Bar dataKey="views" fill="#7f1d1d" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

/* --- Features --- */
function FeatureUsageChart() {
  const [data, setData] = useState([]);
  const [days, setDays] = useState(30);
  useEffect(() => { fetchFeatureUsage(days).then(setData).catch(() => {}); }, [days]);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">Fonctionnalités utilisées</CardTitle>
        <select className="rounded border px-2 py-1 text-xs" value={days}
          onChange={(e) => setDays(Number(e.target.value))} data-testid="features-period">
          <option value={7}>7 j</option>
          <option value={30}>30 j</option>
          <option value={90}>90 j</option>
        </select>
      </CardHeader>
      <CardContent className="h-64" data-testid="chart-features">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            Aucune fonctionnalité journalisée sur cette période.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.slice(0, 10)} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="feature" fontSize={10} angle={-25} textAnchor="end" />
              <YAxis fontSize={10} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="count" fill="#d97706" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

/* --- Countries --- */
function CountriesChart() {
  const [data, setData] = useState([]);
  useEffect(() => { fetchAnalyticsCountries().then(setData).catch(() => {}); }, []);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Top pays (30 j)</CardTitle></CardHeader>
      <CardContent className="h-64" data-testid="chart-countries">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            GeoIP non instrumenté — remplissez <code>country_code</code> dans <code>page_views</code>.
          </div>
        ) : (
          <div className="flex h-full flex-col justify-between gap-1">
            {data.map((c) => (
              <div key={c.country_code} className="flex items-center gap-2 text-xs">
                <span className="w-6">{flag(c.country_code)}</span>
                <span className="font-mono w-10">{c.country_code}</span>
                <div className="flex-1 overflow-hidden rounded bg-muted">
                  <div className="h-4 bg-red-700" style={{ width: `${(c.sessions / data[0].sessions) * 100}%` }} />
                </div>
                <span className="font-mono">{fmtNum(c.sessions)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* --- Devices --- */
function DevicesChart() {
  const [data, setData] = useState([]);
  useEffect(() => { fetchDevices().then(setData).catch(() => {}); }, []);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Appareils (30 j)</CardTitle></CardHeader>
      <CardContent className="h-64" data-testid="chart-devices">
        {data.length === 0 ? (
          <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
            Aucune donnée.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="count" nameKey="device" innerRadius={50} outerRadius={90} label={(e) => `${e.device} ${e.pct}%`}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

/* --- Funnel --- */
function FunnelChart() {
  const [data, setData] = useState([]);
  useEffect(() => { fetchFunnel().then(setData).catch(() => {}); }, []);
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">Entonnoir de conversion (30 j)</CardTitle></CardHeader>
      <CardContent data-testid="chart-funnel">
        <div className="space-y-2">
          {data.map((s, i) => {
            const w = Math.max(8, (s.count / max) * 100);
            return (
              <div key={s.step} className="flex items-center gap-3">
                <div className="w-40 text-xs font-semibold">{s.step}</div>
                <div className="flex-1 overflow-hidden rounded bg-muted">
                  <div
                    className="flex h-8 items-center justify-end px-2 text-xs font-mono text-white"
                    style={{
                      width: `${w}%`,
                      background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[(i + 1) % COLORS.length]})`,
                    }}
                  >
                    {fmtNum(s.count)}
                  </div>
                </div>
                <div className="w-20 text-right font-mono text-xs">
                  {i === 0 ? "—" : `${s.conversion_pct}%`}
                </div>
              </div>
            );
          })}
          {data.length === 0 && (
            <div className="py-8 text-center text-xs text-muted-foreground">Données insuffisantes.</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* --- Errors --- */
function ErrorsTable() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ type: "", resolved: "" });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filter.type) params.error_type = filter.type;
      if (filter.resolved !== "") params.resolved = filter.resolved;
      const r = await fetchErrors(params);
      setRows(r.data || []);
      setTotal(r.total || 0);
    } catch { toast.error("Erreur chargement journal."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filter]);

  async function toggle(id, current) {
    try {
      await resolveErrorLog(id, !current);
      toast.success(current ? "Remise ouverte." : "Marquée comme résolue.");
      load();
    } catch { toast.error("Échec."); }
  }
  function downloadStack(r) {
    const blob = new Blob([
      `[${r.created_at}] ${r.error_type.toUpperCase()} · ${r.page_path || "—"}\n`,
      `Message:\n${r.error_message}\n\nStack:\n${r.stack_trace || "(aucune)"}\n`,
    ], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `error_${r.id}.txt`; a.click();
    URL.revokeObjectURL(url);
  }

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Journal des erreurs ({fmtNum(total)})
        </CardTitle>
        <div className="flex items-center gap-2">
          <select className="rounded border px-2 py-1 text-xs" value={filter.type}
            onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, type: e.target.value })); }}
            data-testid="err-filter-type">
            <option value="">Tous types</option>
            <option value="frontend">frontend</option>
            <option value="backend">backend</option>
            <option value="llm">llm</option>
            <option value="storage">storage</option>
          </select>
          <select className="rounded border px-2 py-1 text-xs" value={filter.resolved}
            onChange={(e) => { setPage(1); setFilter((f) => ({ ...f, resolved: e.target.value })); }}
            data-testid="err-filter-resolved">
            <option value="">Tous statuts</option>
            <option value="false">Non résolu</option>
            <option value="true">Résolu</option>
          </select>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs" data-testid="errors-table">
          <thead>
            <tr className="border-b text-left uppercase text-muted-foreground">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Page</th>
              <th className="px-3 py-2">Message</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b ${r.resolved ? "opacity-60" : ""}`} data-testid={`err-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2">
                  <Badge className={
                    r.error_type === "frontend" ? "bg-blue-600 text-white" :
                    r.error_type === "backend" ? "bg-red-700 text-white" :
                    r.error_type === "llm" ? "bg-violet-600 text-white" : "bg-amber-600 text-white"
                  }>{r.error_type}</Badge>
                </td>
                <td className="px-3 py-2 font-mono text-[10px]">{r.page_path || "—"}</td>
                <td className="px-3 py-2 max-w-md truncate" title={r.error_message}>{r.error_message}</td>
                <td className="px-3 py-2">
                  {r.resolved
                    ? <Badge className="bg-emerald-600 text-white">✅ Résolu</Badge>
                    : <Badge className="bg-red-700 text-white">🔴 Ouvert</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => toggle(r.id, r.resolved)} data-testid={`err-toggle-${r.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadStack(r)} data-testid={`err-dl-${r.id}`}>
                    <DownloadIcon className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Aucune erreur.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
      {total > 25 && (
        <div className="flex items-center justify-center gap-2 py-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Préc.</Button>
          <span className="text-xs">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Suiv.</Button>
        </div>
      )}
    </Card>
  );
}
